import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from '../core/service';
import { environment } from '@/environments/environment';
import { TokenData } from '../core/types';
import { Observable, catchError, finalize, map, of, tap, throwError } from 'rxjs';
import { UserService } from '../../user/core/service';
import { User } from '../../user/core/type';
import { AccessService } from '../../../features/access/services/service';
import { NotificationCenterService } from '@/app/common/services/notification-center/notification-center.service';

@Injectable()
export class AuthApiService implements AuthService {
  defaultUrl = `${environment.apiHost}/auth`;

  private userCache?: any;

  constructor(
    private http: HttpClient,
    private userService: UserService,
    private accessService: AccessService,
    private notificationCenter: NotificationCenterService
  ) {}

  get authenticatedUser() {
    if (this.userCache) {
      this.processUserPermissions(this.userCache);
      return of(this.userCache);
    }

    const jwt = localStorage.getItem('token');

    if (!jwt) {
      const err = new Error('Unknown token');
      return throwError(() => err);
    }

    const { id } = this.parseJwt(jwt);

    return this.userService.getById(id).pipe(
      map((item) => {
        this.processUserPermissions(item);
        this.userCache = item;
        return item;
      })
    );
  }

  isLoggedIn(): Observable<boolean> {
    return this.http.get<void>(this.defaultUrl).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  logout(): Observable<void> {
    return this.http.delete<void>(this.defaultUrl).pipe(
      finalize(() => {
        localStorage.removeItem('token');
        delete this.userCache;
        this.notificationCenter.disconnect();
      }),
      catchError(() => {
        this.notificationCenter.disconnect();
        return of(void 0);
      })
    );
  }

  login(data: { cpf: string; password: string }) {
    return this.http.post<{ jwt: string }>(this.defaultUrl, data);
  }

  recoverRequest(email: string): Observable<void> {
    return this.http.post<void>(`${this.defaultUrl}/recover`, { email });
  }

  recoverValidate(code: string): Observable<void> {
    return this.http.get<void>(`${this.defaultUrl}/recover/${code}`);
  }

  recoverReset(
    code: string,
    pass: string,
    passConfirm: string
  ): Observable<void> {
    return this.http.post<void>(`${this.defaultUrl}/recover/${code}`, {
      pass,
      passConfirm,
    });
  }

  saveJwt(jwt: string): Observable<Pick<TokenData, 'id'>> {
    localStorage.setItem('token', jwt);

    return this.http.get<void>(this.defaultUrl).pipe(
      tap(() => this.notificationCenter.bootstrap()),
      map(() => {
        const { id } = this.parseJwt(jwt);
        return { id };
      }),
      catchError((err) => {
        localStorage.removeItem('token');
        return throwError(() => err);
      })
    );
  }

  private processUserPermissions(user: User) {
    this.accessService.removeUserPermissions();

    this.accessService.addUserPermission(user.RoleModel.name);
  }

  private parseJwt(token: string): TokenData {
    var base64Url = token.split('.')[1];
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );

    return JSON.parse(jsonPayload);
  }
}
